// Proving the second factor (spec/07 §6.6): an authenticator code, or a backup code. The same form
// finishes a password sign-in that stopped at `twoFactorRedirect` and steps up a session the console
// gate refused; either way the server answers with a new session that records the factor.

import { useTranslation } from '@clavian/i18n/react';
import { Button, TextField } from '@clavian/ui-web';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { useAuthFailure } from './auth-errors';

export function SecondFactorForm({ intro, onVerified }: { readonly intro: string; readonly onVerified: () => Promise<void> | void }) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    try {
      const body = { code: code.replace(/\s+/g, '') };
      if (mode === 'totp') await api.verifyTwoFactorTotp({ body });
      else await api.verifyTwoFactorBackupCode({ body });
      await onVerified();
    } catch (error) {
      setFailure(failureText(error));
    }
  });

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <p>{intro}</p>
      {failure ? (
        <p className="console-alert" role="alert">
          {failure}
        </p>
      ) : null}
      <Controller
        control={form.control}
        name="code"
        rules={{ required: t('signIn.required') }}
        render={({ field, fieldState }) => (
          <TextField
            label={mode === 'totp' ? t('secondFactor.code') : t('secondFactor.backupCode')}
            required
            autoComplete="one-time-code"
            {...(mode === 'totp' ? { inputMode: 'numeric' as const, numeric: true } : {})}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            error={fieldState.error?.message}
          />
        )}
      />
      <div className="console-form__actions">
        <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
          {t('secondFactor.confirm')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setMode(mode === 'totp' ? 'backup' : 'totp');
            form.reset({ code: '' });
            setFailure(null);
          }}
        >
          {mode === 'totp' ? t('secondFactor.useBackup') : t('secondFactor.useApp')}
        </Button>
      </div>
    </form>
  );
}
